import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Autosuggest from 'react-autosuggest';

import { Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import nonCriticalException from '../../../../modules/exceptionLogger';
import analytics from '../../../../modules/analytics';
import debouncer from '../../../../modules/debouncer';
import { BodyText, Absolute } from '../../../ui';
import { MapsDirection45Icon } from '../../../ui/Icons/MapsDirection45Icon';
import { MapsPinIcon } from '../../../ui/Icons/MapsPinIcon';
import { useMount } from '../../../../hooks';
import { IRootDispatch, RootState } from '../../../../store/models';
import { LocationRecord, LocationState } from '../../../../store/Location.model';
import { getLocationSuggestions, LocationSuggestionResult } from '../../../../modules/maps/locationAutocomplete';
import { clickable } from '../../../../styles';
import store from '../../../../store';

import './index.scss';

const debounce = debouncer(250);

// Re-query geolocation after 1 minute
// This only applies to "Find Me" user action
const MAX_GEOLOCATION_AGE = 60 * 1000;

type LocationSuggestion = LocationRecord & {
  isCurrentLocation?: boolean;
};

// a special search "suggestion" displayed when nothing else is returned
const CurrentLocationItem: LocationSuggestion = {
  address: 'Current Location',
  isCurrentLocation: true,
  latitude: null,
  longitude: null,
};

enum LocationControlOptions {
  SelectedFindMe = 'find-me',
  EnteredLocationManually = 'user-manually-entered',
  SelectedAutocompleteSuggestion = 'autocomplete-suggestion-selected',
}

enum LocationSource {
  Prefilled = 'text_prefilled',
  Gps = 'gps',
}

/**
 * The location picker popover for search.
 *
 * It includes an autocomplete for locations and functionality to get the user's
 * device location.
 *
 * The location the user selects get sent to the SearchConfig redux state.
 */
type LocationFilterProps = {
  address: string;
  updateLocation: (address: LocationRecord) => void;
  addressComponents?: unknown;
  nextSearchID?: string;
};

export const LocationFilter = React.forwardRef((
  {
    address,
    updateLocation,
    nextSearchID,
  }: LocationFilterProps,
  ref,
) => {
  const dispatch = useDispatch<IRootDispatch>();
  const [locationAddressText, setLocationAddressText] = useState<string>(address);
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>(
    [CurrentLocationItem],
  );
  const [lastTypedText, setLastTypedText] = useState<string>('');
  const [control, setControl] = useState<LocationControlOptions>(null);
  const [focused, setFocused] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<boolean>(null);

  const userLocation = useSelector<RootState, LocationState>(
    state => store.select.location.userLocation(state),
  );
  const searchLocation = useSelector<RootState, LocationRecord>(
    state => store.select.search.searchLocation(state),
  );

  /**
   * Send the `client_locationpicker_submitted` tracking event, plus any additional tracking
   * params.
   * @param {Object} location - Object with location information
   * @param {String} control - A unique key representing the thing that initiated the submit.
   * @param {String} source - The source of the location data
   */
  const trackSubmit = (location: LocationRecord, source: string = 'text_prefilled') => {
    analytics.track('client_locationpicker_submitted', {
      control,
      search_text: locationAddressText,
      select_type: source,
      last_typed_search_text: lastTypedText,
      search_id: nextSearchID,
      location: {
        latitude: location.latitude,
        longitude: location.longitude,
      },
    });
  };

  /**
   * Asynchronously updates the location using the `updateLocation` prop.
   * @param {LocationRecord} location The location information to set
   * @param {string} source The origin of the location information
   */
  const updateAndTrackLocation = async (location: LocationRecord, source) => {
    try {
      await updateLocation(location);
      trackSubmit(location, source);
    } catch (e) {
      nonCriticalException(e);
    }
  };

  const isUserlocationExpired = () => (
    (userLocation.created || 0) < Date.now() - MAX_GEOLOCATION_AGE
  );

  const handleGeolocationUpdate = async () => {
    if (isUserlocationExpired()) {
      await dispatch.location.loadGeolocation();
      await updateAndTrackLocation(searchLocation, LocationSource.Gps);
    }
    setLocationAddressText(searchLocation.address);
    setLocationError(false);
  };

  useMount(() => {
    if (!address) {
      handleGeolocationUpdate();
    }
  });

  const formatSearchResultToLocation = (item: LocationSuggestionResult): LocationRecord => ({
    longitude: null,
    latitude: null,
    ...item,
  });

  /**
   * Return what the input field value should be for an autocomplete suggestion.
   */
  const getSuggestionValue = (suggestion: LocationSuggestion) => suggestion.address;

  const updateLocationSuggestions = async ({ value }) => {
    debounce(async () => {
      if (value === '') setLocationSuggestions([CurrentLocationItem]);

      const results = await getLocationSuggestions(value, searchLocation, 5, true);
      try {
        setLocationSuggestions([
          CurrentLocationItem,
          ...results.map(loc => formatSearchResultToLocation(loc)),
        ]);
      } catch (e) {
        nonCriticalException(e);
      }
    });
  };

  /**
   * A location suggestion was selected.
   * See: https://github.com/moroshko/react-autosuggest#on-suggestion-selected-prop
   * @param {Object} suggestion
   * @param {Boolean} manualSelection - False if location picked from suggestion drop down.
   *                                    True if manually entered and submitted.
   */
  const handleSuggestionSelected = (
    _: never,
    result: { suggestion: LocationSuggestion },
    manualSelection: boolean = false,
  ) => {
    if (!result) return;

    const { suggestion } = result;

    // if user selects "Use current location", use users location
    if (suggestion.isCurrentLocation) {
      setControl(LocationControlOptions.SelectedFindMe);
      setFocused(false);
      if (userLocation?.error) {
        setLocationAddressText('');
        setLocationError(true);
      } else {
        setLocationAddressText(userLocation.address);
        updateAndTrackLocation(userLocation, LocationSource.Gps);
      }
    } else {
      setControl(manualSelection
        ? LocationControlOptions.EnteredLocationManually
        : LocationControlOptions.SelectedAutocompleteSuggestion);
      setFocused(false);
      setLocationError(false);
      setLocationAddressText(suggestion.address);
      updateAndTrackLocation(suggestion, LocationSource.Prefilled);
    }
  };

  /**
   * Clear all the suggestions and just show the current location button.
   */
  const handleClearRequest = () => {
    setLocationSuggestions([CurrentLocationItem]);
  };

  /**
   * Called when the value of the location field changes.
   * See: https://github.com/moroshko/react-autosuggest#input-props-prop
   */
  const handleFieldChange = (evt, { newValue, method }) => {
    if (method === 'escape') {
      setFocused(false);
      evt.target.blur();
    } else if (method !== 'click' || newValue) {
      // update text as long as we're not trying to find current location
      setLocationAddressText(newValue);
      setLastTypedText(newValue);
    }
  };

  const handleFieldFocus = evt => {
    evt.target.select();
    setFocused(true);
    setLocationSuggestions([CurrentLocationItem]);
  };

  const handleFieldBlur = (_: unknown, { highlightedSuggestion }) => {
    if (!highlightedSuggestion && locationSuggestions.length > 1) {
      const suggestion = locationSuggestions[1];
      setControl(LocationControlOptions.EnteredLocationManually);
      setLocationError(false);
      setLocationAddressText(suggestion.address);
      updateAndTrackLocation(suggestion, LocationSource.Prefilled);
    }
    setFocused(false);
  };

  /**
   * Render the header element, which will display the input box and search button.
   */
  const renderInputComponent = inputProps => (
    <header>
      <section className={classNames('location-input')}>
        <Absolute left={11}>
          <MapsPinIcon color={BrandColor.BackInBlack} />
        </Absolute>
        <input {...inputProps} />
      </section>
    </header>
  );

  /**
   * Render an individual location suggestion.
   */
  const renderSuggestion = (suggestion: LocationSuggestion, { isHighlighted }) => {
    const { isCurrentLocation } = suggestion;
    return (
      <Inline
        paddingY={12}
        paddingRight={20}
        paddingLeft={isCurrentLocation ? 24 : 40}
        color={isHighlighted ? BrandColor.LightBlue : BrandColor.WhiteWash}
        borderBottomColor={BrandColor.LightGrey}
        borderBottomWidth={1}
        borderStyle="solid"
        space={4}
        style={clickable}
      >
        {isCurrentLocation && (
          <MapsDirection45Icon
            color={BrandColor.BackInBlack}
            width={16}
            height={16}
          />
        )}
        <BodyText>
          {suggestion.isCurrentLocation
            ? 'Use My Current Location'
            : suggestion.address}
        </BodyText>
      </Inline>
    );
  };

  return (
    <section
      className={classNames('location-filter-component', {
        focused,
        error: locationError,
      })}
    >
      <Autosuggest
        inputProps={{
          value: locationAddressText || '',
          onChange: handleFieldChange,
          onFocus: handleFieldFocus,
          onBlur: handleFieldBlur,
          autoComplete: 'disabled',
          placeholder: locationError
            ? 'Please enable location sharing'
            : 'City or Zip Code',
          'data-testid': 'location-input',
        }}
        ref={ref}
        alwaysRenderSuggestions={focused}
        focusInputOnSuggestionClick={false}
        suggestions={locationSuggestions}
        onSuggestionsFetchRequested={updateLocationSuggestions}
        onSuggestionsClearRequested={handleClearRequest}
        onSuggestionSelected={handleSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
      />
    </section>
  );
});
